/**
 * Line-art emblems for the skills, drawn at the center of each skill's node
 * in the same style as the grouping roots' (`skill-tree-emblems.ts`): the
 * same +/-18-unit box, layers and strokes, tinted with the skill's group color.
 * Each follows its icon concept (docs/gameplay/skill-tree-ui.md, Skill
 * Roster and Icons) and is built around one silhouette so it reads at the
 * node's small size. Crafting skills have none until crafting awakens.
 */

import { SWORD_LINES, SWORD_OUTLINE, type SkillTreeEmblemLayer } from './skill-tree-emblems';

/** A short blade, for pairs. */
const SHORT_BLADE_OUTLINE = 'M0 -19L2 -15.5V3.5H5.8V5.4H1.2V10.2A1.5 1.5 0 1 1 -1.2 10.2V5.4H-5.8V3.5H-2V-15.5Z';
const SHORT_BLADE_LINES = [
    'M0 -19L2 -15.5V3.5H-2V-15.5Z',
    'M-5.8 4.4H5.8',
    'M0 5.4V10.1',
    { d: 'M0 -15.8V2.6', width: 0.6 },
    { circle: [0, 11.7, 1.5] },
] as const;

/** A tongue of flame rising from `inner` to `outer` along -y, for flames rotated around a center. */
function flameTongue(inner: number, outer: number, halfWidth: number): string {
    const mid = (inner + outer) / 2;
    return `M0 ${-inner}Q${halfWidth} ${-mid} 0 ${-outer}Q${-halfWidth} ${-mid} 0 ${-inner}Z`;
}

function ringOf(count: number, line: string, offsetDegrees = 0): SkillTreeEmblemLayer[] {
    return Array.from({ length: count }, (_, index) => ({ rotate: offsetDegrees + (360 / count) * index, lines: [line] }));
}

export const SKILL_TREE_SKILL_EMBLEMS: Readonly<Record<string, readonly SkillTreeEmblemLayer[]>> = {
    // Martial: Melee
    'blade-mastery': [{ knockout: SWORD_OUTLINE, lines: SWORD_LINES }],
    bludgeoning: [
        { lines: ['M-1.6 18.5H1.6V-2.5H-1.6Z', { d: 'M-2.6 13H2.6M-2.6 15.5H2.6', width: 0.6 }] },
        {
            knockout: 'M0 -18A8 8 0 1 1 0 -2A8 8 0 1 1 0 -18Z',
            lines: [
                { circle: [0, -10, 6.2] },
                'M0 -16.2V-20M0 -3.8V-1.5M-6.2 -10H-10M6.2 -10H10',
                'M-4.4 -14.4L-7.2 -17.2M4.4 -14.4L7.2 -17.2M-4.4 -5.6L-7.2 -2.8M4.4 -5.6L7.2 -2.8',
                { circle: [0, -10, 2], width: 0.7 },
            ],
        },
    ],
    // Two daggers crossed point-down, so they don't echo the Melee root's crossed swords.
    'dual-wielding': [
        { rotate: 145, knockout: SHORT_BLADE_OUTLINE, lines: SHORT_BLADE_LINES },
        { rotate: -145, knockout: SHORT_BLADE_OUTLINE, lines: SHORT_BLADE_LINES },
    ],
    unarmed: [
        {
            lines: [
                'M-10 -5Q-10 -12 -4 -12H8Q12.5 -12 12.5 -7V4Q12.5 11 5 11H-3Q-10 11 -10 5Z',
                { d: 'M-4 -12V-5M1 -12V-5M6.5 -12V-5', width: 0.8 },
                { d: 'M-10 -5H12.5', width: 0.8 },
                'M-10 0Q-3 -1.5 1 3.5',
                'M-4 11V18M5 11V18',
            ],
        },
    ],
    // Martial: Ranged
    archery: [
        {
            rotate: -45,
            lines: [
                'M5 -19Q8 -19 8.5 -16Q15 -8 14.5 0Q15 8 8.5 16Q8 19 5 19',
                { d: 'M5.6 -18.5L-9 0L5.6 18.5', width: 0.7 },
                'M-11 0H20',
                'M20 0L16.5 -2.4M20 0L16.5 2.4',
                'M-11 0L-14.5 -3.2M-8 0L-11.5 -3.2M-11 0L-14.5 3.2M-8 0L-11.5 3.2',
            ],
        },
    ],
    // Martial: Defense
    'shield-mastery': [
        {
            lines: [
                { circle: [0, 0, 16] },
                { circle: [0, 0, 11.5], width: 0.7 },
                { circle: [0, 0, 4.2] },
                { circle: [0, 0, 1.6], width: 0.6 },
                { circle: [0, -13.7, 0.6] },
                { circle: [0, 13.7, 0.6] },
                { circle: [-13.7, 0, 0.6] },
                { circle: [13.7, 0, 0.6] },
            ],
        },
    ],
    parry: [
        { rotate: 20, knockout: SWORD_OUTLINE, lines: SWORD_LINES },
        { rotate: -62, knockout: SHORT_BLADE_OUTLINE, lines: SHORT_BLADE_LINES },
        { lines: [{ d: 'M-6 -9.5L-9.5 -13.5M-3 -12L-3.5 -17M-8.5 -6L-13.5 -6.5', width: 0.7 }] },
    ],

    // Fury
    wrath: [
        { lines: [{ circle: [0, 0, 4] }] },
        ...ringOf(8, flameTongue(6.5, 19, 3.2)),
    ],
    frenzy: [
        {
            lines: [
                'M0 15Q-14 5 -12.5 -3Q-11 -9.5 -5 -9Q-1.8 -8.6 0 -4.5Q1.8 -8.6 5 -9Q11 -9.5 12.5 -3Q14 5 0 15Z',
                { d: 'M-8 -10Q-10.5 -14.5 -7.5 -19Q-6.5 -14.5 -4.5 -13', width: 0.8 },
                { d: 'M0 -6.5Q-2.5 -13 0.5 -19.5Q2.5 -13.5 2.5 -10', width: 0.8 },
                { d: 'M8 -10Q10.5 -14.5 7.5 -19Q6.5 -14.5 4.5 -13', width: 0.8 },
                { d: 'M-5 2Q-6 7 -1.5 10', width: 0.7 },
            ],
        },
    ],
    immolation: [
        { lines: [{ circle: [0, 0, 10] }, { circle: [0, 0, 7], width: 0.6, dash: '4 3' }] },
        ...ringOf(6, flameTongue(10.5, 18.5, 2.6)),
        ...ringOf(6, flameTongue(10.5, 15, 1.6), 30),
    ],

    // Courage
    'battle-instincts': [
        {
            lines: [
                'M-17 0Q-5 -10 7 0Q-5 10 -17 0Z',
                { circle: [-5, 0, 4] },
                { circle: [-5, 0, 1.5], width: 0.7 },
                'M11 -6.5L16.5 -10M12 0H18.5M11 6.5L16.5 10',
            ],
        },
    ],
    valor: [
        {
            lines: [
                { circle: [4.5, -13.5, 3] },
                'M4.5 -10V5.5M4.5 5.5L1 17.5M4.5 5.5L8 17.5',
                'M4.5 -8Q-8 -9.5 -14 6',
                { d: 'M4.5 -8L10 -1', width: 0.8 },
                { circle: [-6.5, 4.5, 2.2] },
                { d: 'M-6.5 6.8V12M-6.5 12L-9 17.5M-6.5 12L-4 17.5', width: 0.8 },
            ],
        },
    ],
    smite: [
        { rotate: 180, knockout: SWORD_OUTLINE, lines: SWORD_LINES },
        {
            lines: [
                { d: 'M-12 -19L-8 -10H-11.5L-6.5 -1', width: 0.8 },
                { d: 'M12 -19L8 -10H11.5L6.5 -1', width: 0.8 },
            ],
        },
    ],

    // Sorrow
    restoration: [
        {
            lines: [
                'M0 -18Q5.5 -10 5 -6.5Q4.5 -2 0 -2Q-4.5 -2 -5 -6.5Q-5.5 -10 0 -18Z',
                { d: 'M-2.2 -7Q-2.6 -4.5 -0.8 -3.6', width: 0.7 },
                'M-15 1Q-13 12 0 12Q13 12 15 1',
                { d: 'M15 1Q16.5 -2.5 13.5 -2.5Q12 -0.5 11 2.5M-15 1Q-16.5 -2.5 -13.5 -2.5Q-12 -0.5 -11 2.5', width: 0.8 },
                'M-6 12Q-4.5 16 -6 19M6 12Q4.5 16 6 19',
            ],
        },
    ],
    entropy: [
        {
            lines: [
                'M0 0Q3 -0.5 2.5 -3.5Q1.5 -6.5 -2 -6Q-7 -5 -6.5 0Q-6 6.5 0.5 7Q8 7.5 10 0.5',
                { d: 'M11.5 -3.5L12.6 -6M12.8 -9.5L14.6 -11.2M9.5 -13L10.6 -15.4', width: 0.8 },
                { circle: [16, -15, 0.8] },
                { circle: [6, -17.5, 0.7] },
                { circle: [17.5, -6.5, 0.6] },
            ],
        },
    ],
    // Two pillars on a threshold, three waves of veil held between them.
    parting: [
        {
            lines: [
                'M-13.5 -11V13M-8.5 -11V13M8.5 -11V13M13.5 -11V13',
                'M-15.5 -13H-6.5M6.5 -13H15.5',
                { d: 'M-15 -11H-7M7 -11H15M-15 13H-7M7 13H15', width: 0.8 },
                { d: 'M-11 -9V11M11 -9V11', width: 0.5 },
                'M-18 16H18',
                {
                    d: 'M-5.5 -6Q-2.75 -8.5 0 -6Q2.75 -3.5 5.5 -6M-5.5 0Q-2.75 -2.5 0 0Q2.75 2.5 5.5 0M-5.5 6Q-2.75 3.5 0 6Q2.75 8.5 5.5 6',
                    width: 1,
                },
            ],
        },
    ],

    // Memory
    insight: [
        {
            lines: [
                'M-12 -6L-6 -14L7 -13L14 -3L11 11L-3 14.5L-13 6Z',
                { d: 'M-2 -13.5L1 -6L-1.5 -1.5M1.5 2.5L4 8.5L2 14', width: 0.8 },
                { circle: [0, 0.5, 2.4] },
                { d: 'M0 -4V-5.5M4 0.5H5.5M-4 0.5H-5.5M0 5V6.5', width: 0.6 },
            ],
        },
    ],
    echo: [
        {
            lines: [
                { d: 'M3 -13L13 0L3 13', width: 1.2 },
                { d: 'M-4 -13L6 0L-4 13', width: 0.8 },
                { d: 'M-11 -13L-1 0L-11 13', width: 0.5 },
                { d: 'M-18 -13L-8 0L-18 13', width: 0.3 },
            ],
        },
    ],

    // Truth
    warding: [
        {
            lines: [
                'M-17 -16H17',
                'M-15 -16Q-5 -4 -11 17',
                'M15 -16Q5 -4 11 17',
                { d: 'M-15 -16Q-9 -2 -14 17M15 -16Q9 -2 14 17', width: 0.6 },
                'M-6.5 2Q0 -4 6.5 2Q0 8 -6.5 2Z',
                { circle: [0, 2, 2], width: 0.8 },
            ],
        },
    ],
    'veilguard': [
        {
            lines: [
                'M0 -19.5L-2.4 -13.5H2.4L0 -7',
                'M0 -7Q-3.5 -2.5 -15 -2.5M0 -7Q3.5 -2.5 15 -2.5',
                { d: 'M-15 -2.5L-12 -5M15 -2.5L12 -5', width: 0.7 },
                'M0 1L7 8.5L0 16.5L-7 8.5Z',
                { d: 'M0 4V13M-3.5 8.5H3.5', width: 0.7 },
            ],
        },
    ],

    // Resonance
    // Three threads twisting down a narrowing funnel into an arrowhead, over the point they draw into.
    attune: [
        {
            lines: [
                'M0 -19C1.5 -18.5 7.3 -16.8 9.2 -15.7C11 -14.7 11.4 -13.6 10.9 -12.5C10.5 -11.4 8.2 -10.3 6.4 -9.2C4.5 -8.2 1.7 -7.1 0 -6C-1.7 -4.9 -3.1 -3.8 -3.7 -2.7C-4.3 -1.7 -3.9 -0.6 -3.5 0.5C-3.1 1.6 -1.9 2.7 -1.4 3.8C-0.8 4.8 -0.2 6.5 0 7',
                'M13 -19C11.4 -18.5 6.4 -16.8 3.4 -15.7C0.3 -14.7 -3.5 -13.6 -5.5 -12.5C-7.5 -11.4 -8.6 -10.3 -8.7 -9.2C-8.8 -8.2 -7.4 -7.1 -6.1 -6C-4.9 -4.9 -2.7 -3.8 -1.4 -2.7C0 -1.7 1.2 -0.6 1.8 0.5C2.3 1.6 2.1 2.7 1.9 3.8C1.6 4.8 0.7 6.5 0.5 7',
                'M-13 -19C-12.9 -18.5 -13.8 -16.8 -12.5 -15.7C-11.3 -14.7 -7.9 -13.6 -5.5 -12.5C-3 -11.4 0.4 -10.3 2.3 -9.2C4.3 -8.2 5.7 -7.1 6.1 -6C6.6 -4.9 5.8 -3.8 5.1 -2.7C4.4 -1.7 2.7 -0.6 1.8 0.5C0.8 1.6 -0.1 2.7 -0.5 3.8C-0.9 4.8 -0.5 6.5 -0.5 7',
                { d: 'M0 13.5L4.6 6Q0 9 -4.6 6Z', solid: true },
                { circle: [0, 18.5, 1.6], solid: true },
            ],
        },
    ],
    channel: [
        {
            lines: [
                'M-16 -11Q-9.5 -12 -9 -5V5Q-9.5 12 -16 11',
                { d: 'M-9 -5L-5.5 -7.5M-9 -1L-5 -2.5M-9 3L-5.5 2.5', width: 0.7 },
                'M-1 -9Q3.5 0 -1 9',
                { d: 'M5 -13.5Q11.5 0 5 13.5', width: 0.8 },
                { d: 'M11 -17.5Q19.5 0 11 17.5', width: 0.6 },
            ],
        },
    ],
    harmony: [
        {
            lines: [
                'M-11 -18Q-11 -7 0 1',
                'M0 -18Q3 -9 0 1',
                'M11 -18Q11 -7 0 1',
                'M0 1Q4 5.5 0 9.5Q-4 13.5 0 18',
                { d: 'M0 1Q-4 5.5 0 9.5Q4 13.5 0 18', width: 0.7 },
            ],
        },
    ],
};
